<template>
  <div ref="pageContainer">
    <MdmSettingsHeader
      v-if="shouldRenderSettings"
      v-show="!showLoadingOverlay"
      @mounted="handleHeaderMounted"
    />
    <div :class="$style.container">
      <GettingStartedPanel
        :buttonLabel="buttonCta.text"
        :itemType="itemType"
        :visible="shouldRenderGettingStarted"
        @ctaButtonClick="handleGettingStartedCta()"
      />
      <MdmSettings
        v-if="shouldRenderSettings"
        v-show="!showLoadingOverlay"
        @dep-renew="handleForm(depRenew)"
        @dep-setup="handleForm(depSetup)"
        @mdm-delete="handleMdmDeleted"
        @renew-cert="handleForm(certRenew)"
      />
      <MdmSetupForm
        v-if="shouldRenderSetup"
        v-show="!showLoadingOverlay"
        :dmId="getDmId"
        :itemType="certSetupType"
        @back-button="handleBackButton"
        @file-uploaded="handleFileUpload"
        @setup-completed="handleSetupCompleted"
      />
      <LoadingOverlay
        :showLoadingOverlay="showLoadingOverlay"
        :zIndex="Number(loadingOverlayZIndex)"
      />
    </div>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  ItemTypeConsts,
  MdmFormsTypes,
  StoreModelMap,
  UnsavedChangesModalConsts,
} from 'client/util/Constants';
import { loadingOverlayZIndex } from 'client/css/z-index.css';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import GettingStartedPanel from 'client/components/GettingStartedPanel/GettingStartedPanel.vue';
import LoadingOverlay from 'client/components/LoadingOverlay/LoadingOverlay.vue';
import MainPanelMixin from 'components/MainPanel/mixins/MainPanelMixin';
import MdmSettings from 'client/MDM/MdmSettings.vue';
import MdmSettingsHeader from 'client/MDM/MdmSettingsHeader';
import MdmSetupForm from 'client/MDM/MdmSetupForm';
import ModalEventBus from 'components/Modal/ModalEventBus';

const mdmSettingsModel = StoreModelMap[ItemTypeConsts.mdmSettings];
const { error } = Constants.default;
const { headerText, bodyText } = UnsavedChangesModalConsts;
const {
  certRenew, certSetup, depRenew, depSetup,
} = MdmFormsTypes;

export default {
  name: 'MdmSettingsMainPanel',

  components: {
    GettingStartedPanel,
    LoadingOverlay,
    MdmSettings,
    MdmSettingsHeader,
    MdmSetupForm,
  },

  mixins: [MainPanelMixin],

  data: () => ({
    buttonCta: {
      text: 'Configure MDM',
    },
    certSetupType: '',
    certRenew,
    certSetup,
    depRenew,
    depSetup,
    fileUploaded: false,
    loadingOverlayZIndex,
    shouldRenderSettings: false,
    shouldRenderSetup: false,
    shouldRenderGettingStarted: false,
    showLoadingOverlay: true,

  }),

  computed: {
    ...mapState(mdmSettingsModel, [
      'item',
    ]),

    ...mapGetters(mdmSettingsModel, [
      'isApnsPushTopicEmpty',
      'getDmId',
    ]),
  },

  watch: {
    isApnsPushTopicEmpty(value) {
      this.shouldRenderSettings = !value;
    },
  },

  async mounted() {
    await this.handleMounted();
  },

  beforeDestroy() {
    this.handleBeforeDestroy();
  },

  methods: {
    ...mapActions(mdmSettingsModel, [
      'createMdmSettings',
      'fetchMdmSettings',
      'resetItem',
    ]),

    handleBackButton() {
      if (!this.fileUploaded) {
        this.shouldRenderSetup = false;
        this.shouldRenderSettings = !this.isApnsPushTopicEmpty;
        this.showLoadingOverlay = !this.isApnsPushTopicEmpty;
        this.shouldRenderGettingStarted = this.isApnsPushTopicEmpty;
      } else {
        this.showConfirmationModal();
      }
    },

    /**
     * @description Reset the models to their default state on exit
     */
    handleBeforeDestroy() {
      this.resetItem();
    },

    handleFileUpload(value) {
      this.fileUploaded = value;
    },

    handleForm(formType) {
      this.$refs.pageContainer.scrollTop = 0;
      this.certSetupType = formType;
      this.shouldRenderSettings = false;
      this.shouldRenderSetup = true;
    },

    async handleGettingStartedCta() {
      this.shouldRenderGettingStarted = false;
      this.triggerButtonClickedEvent('Configure MDM button');
      await this.handleSetup();
      this.handleForm(this.certSetup);
    },

    handleHeaderMounted() {
      this.showLoadingOverlay = false;
    },

    handleMdmDeleted() {
      this.fetchMdmSettings();
      this.shouldRenderGettingStarted = true;
      this.shouldRenderSettings = false;
    },

    async handleMounted() {
      await this.fetchMdmSettings();
      this.handleSetup();
      this.shouldRenderGettingStarted = this.isApnsPushTopicEmpty;
      this.shouldRenderSettings = !this.isApnsPushTopicEmpty;

      if (this.shouldRenderGettingStarted) {
        this.showLoadingOverlay = false;
      }
    },

    handleSetup() {
      if (!this.getDmId) {
        this.createMdmSettings().catch((exp) => {
          notify(error, `Unable to create MDM settings: ${exp.toString()}`);
        });
      }
    },

    handleSetupCompleted() {
      this.$refs.pageContainer.scrollTop = 0;
      this.showLoadingOverlay = true;
      this.fileUploaded = false;
      this.shouldRenderSettings = true;
      this.shouldRenderSetup = false;
      this.fetchMdmSettings();
    },

    hasModelChanged() {
      return this.fileUploaded;
    },

    showConfirmationModal() {
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          bodyText,
          handleContinue: () => {
            this.shouldRenderSettings = true;
            this.shouldRenderSetup = false;
            this.fileUploaded = false;
            ModalEventBus.close();
          },
          headerText,
        },
        preventCloseOnOverlayClick: true,
      });
    },

    triggerButtonClickedEvent(section) {
      EventHelper.triggerMdmButtonClicked({
        section,
      });
    },
  },
};
</script>

<style module>
.container {
  margin: 50px 20px 4.5rem;
}
</style>
